// @ts-nocheck
// Shared per-customer disposition-history cache (API_CUSTOMER_DISPOSITION_
// HISTORY), one instance owned by AgentPortalPage.tsx and consumed by BOTH
// Dialer.tsx's recent-calls remark chips AND DispositionCard.tsx's
// "Previously saved on this caller" list — a single fetch/cache path
// instead of two independent copies, so the same number is never queried
// twice just because two different pieces of UI wanted a look at it.
import { useCallback, useRef, useState } from 'react';
import { getApiCall } from '@/service/httpCall';
import { API_CUSTOMER_DISPOSITION_HISTORY } from '@/service/url';

const BULK_LOOKUP_LIMIT = 20;
const BULK_LOOKUP_CONCURRENCY = 3;

function lastTen(value) {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length > 10 ? digits.slice(-10) : digits;
}

// The backend endpoint requires exactly a 10-digit customer number and
// 400s on anything else (an extension, an "Unknown" placeholder with no
// digits, a short internal/IVR number some call records carry). Bailing
// out here — before ever calling the API — avoids both the failed
// request itself and the retry churn: ensureFetch/requestNumbers mark a
// failed lookup for retry on the next render, so a call record with a
// permanently-malformed number would otherwise keep re-firing the same
// doomed request indefinitely.
function isFetchableCustomerNumber(number) {
  return typeof number === 'string' && number.length === 10;
}

export function useCustomerHistoryCache() {
  const cacheRef = useRef(new Map()); // number -> history[] | 'pending'
  // Numbers whose last fetch attempt errored — cached as `[]` above so a
  // transient failure doesn't loop forever, but tracked separately so a
  // later call/re-render gets to retry instead of being stuck blank for the
  // rest of the session (the old code cached the [] permanently and treated
  // it identically to "genuinely no history", so an outbound call to a
  // number whose very first lookup failed — a slow/cold network request
  // right at dial time is the likeliest moment for that — never got another
  // chance).
  const erroredRef = useRef(new Set());
  const [, forceRender] = useState(0);

  const ensureFetch = useCallback((number) => {
    if (!isFetchableCustomerNumber(number)) return;
    if (cacheRef.current.has(number) && !erroredRef.current.has(number)) return;
    erroredRef.current.delete(number);
    cacheRef.current.set(number, 'pending');
    (async () => {
      try {
        const resp = await getApiCall(API_CUSTOMER_DISPOSITION_HISTORY, { number, limit: 10 });
        cacheRef.current.set(number, Array.isArray(resp?.history) ? resp.history : []);
      } catch {
        cacheRef.current.set(number, []);
        erroredRef.current.add(number);
      }
      forceRender((v) => v + 1);
    })();
  }, []);

  const requestNumbers = useCallback((rawNumbers, limit = BULK_LOOKUP_LIMIT, concurrency = BULK_LOOKUP_CONCURRENCY) => {
    const numbers = [];
    const seen = new Set();
    for (const raw of rawNumbers || []) {
      if (numbers.length >= limit) break;
      const number = lastTen(raw);
      if (isFetchableCustomerNumber(number) && !seen.has(number) && (!cacheRef.current.has(number) || erroredRef.current.has(number))) {
        seen.add(number);
        numbers.push(number);
      }
    }
    if (!numbers.length) return;
    numbers.forEach((n) => { erroredRef.current.delete(n); cacheRef.current.set(n, 'pending'); });
    let nextIndex = 0;
    const runWorker = async () => {
      for (;;) {
        const i = nextIndex++;
        if (i >= numbers.length) return;
        const number = numbers[i];
        try {
          const resp = await getApiCall(API_CUSTOMER_DISPOSITION_HISTORY, { number, limit: 10 });
          cacheRef.current.set(number, Array.isArray(resp?.history) ? resp.history : []);
        } catch {
          cacheRef.current.set(number, []);
          erroredRef.current.add(number);
        }
        forceRender((v) => v + 1);
      }
    };
    Promise.all(Array.from({ length: Math.min(concurrency, numbers.length) }, runWorker));
  }, []);

  const getHistory = useCallback((rawNumber) => {
    const number = lastTen(rawNumber);
    const history = cacheRef.current.get(number);
    if (history === undefined || (Array.isArray(history) && erroredRef.current.has(number))) ensureFetch(number);
    return Array.isArray(history) ? history : [];
  }, [ensureFetch]);

  const isHistoryPending = useCallback((rawNumber) => {
    const number = lastTen(rawNumber);
    return !number || cacheRef.current.get(number) === 'pending' || !cacheRef.current.has(number);
  }, []);

  const getRemarkForCall = useCallback((item) => {
    const history = cacheRef.current.get(lastTen(item.dialNum || item.num));
    if (!Array.isArray(history) || !history.length) return '';
    let entry = item.callSid ? history.find((h) => h?.call_sid === item.callSid) : null;
    if (!entry) {
      const callMs = item.rawTime ? new Date(item.rawTime).getTime() : NaN;
      let bestDiff = Infinity;
      for (const h of history) {
        const hMs = new Date(h?.created_at).getTime();
        if (Number.isNaN(hMs)) continue;
        const diff = Number.isNaN(callMs) ? 0 : Math.abs(hMs - callMs);
        if (diff < bestDiff) { bestDiff = diff; entry = h; }
      }
    }
    const statuses = Array.isArray(entry?.remarks_status) ? entry.remarks_status.filter(Boolean) : [];
    return statuses.join(', ');
  }, []);

  return { requestNumbers, getHistory, getRemarkForCall, isHistoryPending };
}
